import type { Location } from "./types";

/**
 * RESTRICTED-AIRSPACE LOCATIONS — Operating Brief §8.5, approved at Gate A.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THESE PAGES EXIST
 *
 * These are the places where Fly4MEdia cannot fly. Publishing a page that says
 * "drone photography in Banff" and implies routine service would be a false
 * claim (Iron Law 5) and, worse, would sell a shoot that cannot legally happen.
 *
 * But the query exists. Someone is asking whether they can get aerial footage
 * of Lake Louise, and an answer engine is going to cite whoever answers it
 * properly. Almost no competitor writes this page, because it does not sell
 * anything directly. That is exactly why it is the most citable content in the
 * cluster — and it converts, because a business that tells you when it cannot
 * help is a business you trust when it says it can.
 *
 * Every entry here renders the restricted archetype: the prohibition first, the
 * authority named, the permit reality stated, then what CAN legally be shot and
 * where we can fly instead.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The authority for the national-park entries is Parks Canada, which prohibits
 * launching, landing and operating a drone inside a national park without a
 * permit. Those permits are issued for restricted purposes; routine commercial
 * listing and marketing work is not ordinarily one of them. Readers are pointed
 * at Parks Canada directly rather than asked to take our word for it.
 */
export const RESTRICTED_LOCATIONS: Location[] = [
  {
    slug: "banff",
    name: "Banff",
    region: "banff-area",
    city: "Banff",
    province: "Alberta",
    coordinates: { lat: 51.1784, lng: -115.5708 },
    tier: 3,
    driveMinutesFromCalgary: 90,
    airspace: {
      classification: "Banff National Park — Parks Canada permit regime",
      nearestAerodrome: "Banff townsite heliport operations (park-managed)",
      distanceToAerodromeKm: 3,
      authorizationRequired: true,
      authorizationNote:
        "Banff townsite lies inside Banff National Park. Parks Canada prohibits launching, landing and operating a drone within the park without a permit, and those permits are issued for restricted purposes that do not ordinarily include commercial listing or marketing work.",
      source: "parks-canada",
      lastVerified: "2026-08-31",
      restrictedZones: ["Banff National Park (entire townsite and surrounds)"],
    },
    flightStatus: "permit-required",
    airspaceFact: {
      hook: "Banff is the most photographed landscape in Canada and, for a drone operator, effectively closed — the entire townsite sits inside a national park.",
      consequence:
        "Any quote promising routine aerial coverage of Banff is either uninformed or planning to fly illegally, and the liability for that lands on the client's project.",
      resolution:
        "What is legal is shot from outside the park boundary, and the nearest lawful aerial vantages in the Bow Valley are identified before anything is booked.",
    },
    landmarks: [
      "Banff Avenue",
      "the Bow River",
      "Cascade Mountain",
      "Tunnel Mountain",
      "the Trans-Canada Highway corridor",
    ],
    terrain:
      "A mountain townsite on the Bow River, ringed by peaks rising well over a kilometre above the valley floor.",
    windNote:
      "Bow Valley funnelling, with strong diurnal variation between valley floor and slope.",
    seasonalWindow:
      "Academic for aerial work inside the park, since the constraint is legal rather than meteorological.",
    bylawNote:
      "Parks Canada national-park regulations govern the entire area. Municipal rules sit beneath that and do not create an exception.",
    subjectMix: ["tourism-hospitality", "resort-terrain"],
    mobilisationNote:
      "About 90 minutes from the Calgary base — but the drive is not the constraint. The park boundary is.",
    faqs: [
      {
        question: "Can you fly a drone in Banff?",
        answer:
          "No. Banff townsite lies inside Banff National Park, and Parks Canada prohibits launching, landing and operating a drone anywhere in the park without a permit. Those permits are issued for restricted purposes and do not ordinarily cover commercial listing or marketing work.",
      },
      {
        question: "Can a permit be obtained for a commercial drone shoot in Banff?",
        answer:
          "Rarely, and not on a normal project timeline. Parks Canada issues drone permits for restricted purposes such as science and park management. A commercial marketing shoot is not a purpose those permits are ordinarily granted for, so it should not be planned around.",
      },
      {
        question: "What aerial footage of the Banff area is actually legal?",
        answer:
          "Anything flown from outside the park boundary. The Bow Valley around Canmore offers lawful aerial vantages with comparable mountain scale, and those are identified and flown legally rather than promised and then quietly skipped.",
      },
      {
        question: "Does an Advanced RPAS certificate allow flight in a national park?",
        answer:
          "No. Transport Canada certification governs airspace; Parks Canada governs the land. An Advanced certificate permits controlled-airspace operations but does not override a national-park prohibition, and no level of certification does.",
      },
    ],
    primaryKeywords: [
      "drone photography Banff rules",
      "can you fly a drone in Banff",
      "Banff National Park drone permit",
    ],
    nearestLocations: ["canmore", "harvie-heights", "lake-louise", "castle-junction", "exshaw"],
  },

  {
    slug: "lake-louise",
    name: "Lake Louise",
    region: "banff-area",
    city: "Improvement District No. 9",
    province: "Alberta",
    coordinates: { lat: 51.4254, lng: -116.1773 },
    tier: 4,
    driveMinutesFromCalgary: 120,
    airspace: {
      classification: "Banff National Park — Parks Canada permit regime",
      nearestAerodrome: "None within the park boundary",
      distanceToAerodromeKm: 0,
      authorizationRequired: true,
      authorizationNote:
        "Lake Louise lies within Banff National Park. The Parks Canada prohibition on launching, landing and operating a drone applies in full.",
      source: "parks-canada",
      lastVerified: "2026-08-31",
      restrictedZones: ["Banff National Park"],
    },
    flightStatus: "permit-required",
    airspaceFact: {
      hook: "Lake Louise is among the most-searched aerial subjects in Canada, and almost every sweeping drone shot of it circulating online was flown either under a specific Parks Canada permit or illegally.",
      consequence:
        "Commissioning that shot without knowing which category it falls into puts the liability on the commissioning brand, not the operator.",
      resolution:
        "The legal position is stated up front and the alternative vantages outside the park boundary are proposed before any quote is issued.",
    },
    landmarks: [
      "Lake Louise",
      "Victoria Glacier",
      "Moraine Lake road",
      "the Bow Valley Parkway",
      "Lake Louise ski area",
    ],
    terrain:
      "A glacial lake in a high cirque, ringed by peaks and ice. Extreme vertical relief in every direction.",
    windNote:
      "High-altitude cirque conditions with rapid change — operationally demanding even where flight is permitted.",
    seasonalWindow:
      "Not applicable to aerial work inside the park; the constraint is legal, not seasonal.",
    bylawNote: "Parks Canada national-park regulations govern the entire area.",
    subjectMix: ["tourism-hospitality", "resort-terrain"],
    mobilisationNote:
      "About two hours from the Calgary base, entirely inside the park boundary.",
    faqs: [
      {
        question: "Can you fly a drone at Lake Louise?",
        answer:
          "No. Lake Louise sits inside Banff National Park, where Parks Canada prohibits launching, landing and operating a drone without a permit. The prohibition applies to the lake, the shoreline and the surrounding slopes without exception.",
      },
      {
        question: "How were the aerial shots of Lake Louise online actually filmed?",
        answer:
          "Either under a specific Parks Canada permit issued for a restricted purpose, from a crewed aircraft operating legally at altitude, or illegally. Commissioning imagery without establishing which applies transfers the risk to the commissioning brand.",
      },
      {
        question: "What is the closest legal aerial alternative to Lake Louise?",
        answer:
          "The Bow Valley outside the park boundary, around Canmore and Exshaw, offers lawful vantages with genuine mountain scale. It is not the same lake, and any proposal that pretends otherwise is not worth accepting.",
      },
    ],
    primaryKeywords: [
      "can you fly a drone at Lake Louise",
      "Lake Louise drone rules",
      "Lake Louise aerial photography permit",
    ],
    nearestLocations: ["banff", "castle-junction", "canmore", "harvie-heights", "exshaw"],
  },

  {
    slug: "kananaskis-village",
    name: "Kananaskis Village",
    region: "banff-area",
    city: "Kananaskis Improvement District",
    province: "Alberta",
    coordinates: { lat: 50.92, lng: -115.15 },
    tier: 4,
    driveMinutesFromCalgary: 80,
    airspace: {
      classification: "Provincial park land — Alberta Parks restrictions",
      nearestAerodrome: "Kananaskis (Village) heliport operations",
      distanceToAerodromeKm: 2,
      authorizationRequired: true,
      authorizationNote:
        "Kananaskis Village sits on provincial park land within Kananaskis Country. Alberta Parks restrictions govern drone use here, and they are separate from — and additional to — Transport Canada airspace rules.",
      source: "parks-canada",
      lastVerified: "2026-08-31",
      restrictedZones: ["Kananaskis Country provincial park land"],
    },
    flightStatus: "permit-required",
    airspaceFact: {
      hook: "Kananaskis is provincial park land, not a national park, so people routinely assume the Banff prohibition does not apply and that the airspace is therefore open.",
      consequence:
        "Alberta Parks has its own restrictions, and being outside a national park is not the same as being unrestricted — the assumption is where most Kananaskis drone problems begin.",
      resolution:
        "The provincial restriction is checked against the specific site and purpose before anything is quoted, rather than inferred from the absence of a national-park boundary.",
    },
    landmarks: [
      "Kananaskis Village",
      "Mount Kidd",
      "the Kananaskis Trail (Highway 40)",
      "Nakiska",
      "the Kananaskis River",
    ],
    terrain:
      "A mountain resort valley on the Kananaskis River, ringed by front-range peaks with substantial vertical relief.",
    windNote: "Front-range valley funnelling with sharp diurnal shifts.",
    seasonalWindow:
      "Secondary to the regulatory position, which is the governing constraint.",
    bylawNote:
      "Alberta Parks provincial regulations govern Kananaskis Country. These are distinct from national-park rules and from Transport Canada airspace rules.",
    subjectMix: ["resort-terrain", "tourism-hospitality"],
    mobilisationNote:
      "About 80 minutes from the Calgary base on Highway 40, subject to seasonal road closures.",
    faqs: [
      {
        question: "Can you fly a drone in Kananaskis?",
        answer:
          "Not freely. Kananaskis Country is provincial park land and Alberta Parks restrictions govern drone use there. It is a different regime from Banff National Park, but being outside a national park does not make the airspace open.",
      },
      {
        question: "Is Kananaskis treated the same as Banff for drone rules?",
        answer:
          "No, and that difference is the source of most confusion. Banff is federal land under Parks Canada; Kananaskis is provincial land under Alberta Parks. Both restrict drone use, through separate rules with separate processes.",
      },
      {
        question: "Do Transport Canada rules cover provincial park restrictions?",
        answer:
          "No. Transport Canada governs the airspace; Alberta Parks governs the land beneath it. An operator can be fully compliant with RPAS regulations and still be prohibited from launching in a provincial park.",
      },
    ],
    primaryKeywords: [
      "can you fly a drone in Kananaskis",
      "Kananaskis drone rules Alberta",
      "Kananaskis Country aerial photography restrictions",
    ],
    nearestLocations: ["canmore", "dead-mans-flats", "bragg-creek", "morley", "exshaw"],
  },

  {
    slug: "morley",
    name: "Morley",
    region: "bow-valley",
    city: "Stoney Nakoda Nation",
    province: "Alberta",
    coordinates: { lat: 51.16, lng: -114.85 },
    tier: 4,
    driveMinutesFromCalgary: 55,
    airspace: {
      classification: "Class G uncontrolled airspace over First Nation land",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 48,
      authorizationRequired: false,
      authorizationNote:
        "The airspace here is uncontrolled, but the land is Stoney Nakoda Nation land. Flying over it is a matter of consent from the Nation, which is not a Transport Canada or municipal question and cannot be resolved by an airspace check.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Stoney Nakoda Nation land"],
    },
    flightStatus: "prohibited",
    airspaceFact: {
      hook: "The airspace over Morley is uncontrolled, which means a standard airspace check returns a green light and tells you nothing that matters.",
      consequence:
        "The land is Stoney Nakoda Nation land, and consent to fly over it comes from the Nation — an operator who treats a clear NAV Drone result as permission has skipped the only step that counts.",
      resolution:
        "No work is undertaken here without the Nation's consent, arranged directly and in advance. Where that consent is not in place, the work is declined rather than quietly flown.",
    },
    landmarks: [
      "the Bow River",
      "Highway 1A",
      "the Trans-Canada Highway corridor",
      "Morley Flats",
    ],
    terrain:
      "Open Bow valley bench land between the foothills and the front ranges, with the river running through it.",
    windNote: "Exposed valley bench with strong chinook funnelling from the west.",
    seasonalWindow:
      "Secondary to the consent question, which governs whether any flight happens at all.",
    bylawNote:
      "Stoney Nakoda Nation governs land use. Consent is sought from the Nation directly and is not substitutable with any federal or municipal permission.",
    subjectMix: ["agricultural"],
    mobilisationNote:
      "About 55 minutes west of the Calgary base — but mobilisation is not the question here. Consent is.",
    faqs: [
      {
        question: "Can you fly a drone over Morley?",
        answer:
          "Not without the Stoney Nakoda Nation's consent. The airspace itself is uncontrolled, so an airspace check returns clear, but the land is Nation land and permission to operate over it comes from the Nation directly.",
      },
      {
        question: "Does a clear NAV Drone result mean you can fly anywhere?",
        answer:
          "No. NAV Drone answers an airspace question only. Land ownership, First Nation jurisdiction, park regimes and municipal bylaws all sit outside it, and each can prohibit a flight that the airspace check permits.",
      },
      {
        question: "Who grants permission to fly over First Nation land?",
        answer:
          "The Nation itself. It is not a Transport Canada, provincial or municipal decision, and no level of RPAS certification substitutes for it. Where consent is not in place, the work is declined.",
      },
    ],
    primaryKeywords: [
      "drone rules Stoney Nakoda Nation land",
      "can you fly a drone over Morley Alberta",
      "First Nation land drone permission Alberta",
    ],
    nearestLocations: ["ghost-lake", "exshaw", "canmore", "cochrane", "kananaskis-village"],
  },
];
